/**
 * Schema registry — maps LN2E envelope schema hashes to FlatBuffer type
 * handlers for automatic deserialization of HTTP API responses.
 *
 * Each entry associates a schema hash (SHA-256 of the .bfbs binary schema,
 * hex-encoded) with the functions needed to verify and unpack a FlatBuffer
 * root table of that type.
 *
 * The registry is populated at module load time from the embedded .bfbs
 * bytes in the generated TypeScript files (produced by `--bfbs-gen-embed`).
 *
 * Usage:
 *   const handler = schemaRegistry.get(schemaHash);
 *   if (handler) {
 *     handler.verify(payload);           // throws on structural error
 *     const data = handler.unpack(payload); // → *T object
 *   }
 */

import * as flatbuffers from "flatbuffers";
import { sha256Hex } from "../sha256";

/** A registered FlatBuffer type handler for a specific schema hash. */
export interface SchemaHandler {
  /** Human-readable name for debugging (e.g. "PaginatedBanks"). */
  name: string;
  /**
   * The fully qualified name the hash is the SHA-256 of (`Api.PaginatedBanks`),
   * set at registration: the type the schema API is asked about when a server
   * built with another API schema sends one.
   */
  typeName?: string;
  /**
   * Verify structural integrity of the FlatBuffer payload.
   * Throws on malformed data (required fields missing, out-of-bounds, etc.).
   */
  verify: (payload: Uint8Array) => void;
  /**
   * Deserialize the FlatBuffer binary payload to a `*T` Object API instance.
   * Returns a plain JavaScript object suitable for RTK Query caching.
   */
  unpack: (payload: Uint8Array) => unknown;
}

/** The global schema registry, keyed by hex-encoded SHA-256 schema hash. */
const registry = new Map<string, SchemaHandler>();

/**
 * Registers a FlatBuffer type handler for a specific schema hash.
 *
 * Called during module initialization for each API response type.
 * Duplicate registrations for the same hash are silently overwritten
 * (allows hot-reload during development).
 */
export function registerSchema(schemaHash: string, handler: SchemaHandler): void {
  registry.set(schemaHash, handler);
}

/**
 * Looks up the handler for a given schema hash.
 * Returns `undefined` if no handler is registered for this hash.
 */
export function getSchemaHandler(schemaHash: string): SchemaHandler | undefined {
  return registry.get(schemaHash);
}

/**
 * Computes the SHA-256 hash of a .bfbs binary schema for registry keying.
 *
 * Returns the hash as a lowercase 64-char hex string matching the format used
 * by the Go server's `ComputeSchemaHash()`. Goes through {@link sha256Hex}
 * rather than `crypto.subtle` directly: that API is absent outside a secure
 * context, and an offline edge served over http:// at a LAN address is not one,
 * so every client but a browser on the box itself would register no handlers
 * and decode nothing.
 */
export async function computeSchemaHash(bfbs: Uint8Array): Promise<string> {
  return sha256Hex(bfbs);
}

/**
 * Helper to create a standard unpack function from a generated FlatBuffer
 * root type accessor. Handles ByteBuffer creation and size-prefix detection.
 *
 * @param getRootFn  The generated `getRootAsXxx` static function.
 * @param unpackFn   Optional custom unpack — defaults to calling `.unpack()` on the root.
 */
export function createUnpacker<T extends { unpack(): unknown }>(
  getRootFn: (bb: flatbuffers.ByteBuffer) => T,
): (payload: Uint8Array) => unknown {
  return (payload: Uint8Array) => {
    const bb = new flatbuffers.ByteBuffer(payload);
    const root = getRootFn(bb);
    return root.unpack();
  };
}

/**
 * Returns the number of registered schema handlers.
 * Useful for diagnostics and testing.
 */
export function registeredSchemaCount(): number {
  return registry.size;
}
